/**
 * "Share with client" on a finished audit of a Quixo internal workspace (owner decision 3 Nov 2026).
 * GET /v1/growth-audit/jobs/:id/share answers { internal: false } for every other workspace, which renders nothing.
 * Sharing makes the report appear, view and download only, in the portal of every client whose verified owner email is
 * at the audited domain, including clients who sign up later; existing clients get a sign-in link by email.
 */
import { useState } from 'react';
import { Badge, Button, Card, CardHead, Text } from '@qm/ui';
import { useApp } from '../../../lib/app-context';
import { bffJson } from '../../../lib/bff';
import { useFormat } from '../../../lib/format';
import { useResource } from '../../../lib/resource';
import { auditErrorKey } from './audit-view';

export interface ShareState {
  internal: boolean;
  host?: string | null;
  refusal?: 'unreadable_host' | 'free_mail_host' | 'internal_host' | null;
  ready?: boolean;
  share?: { approvedAt: string; approvedBy: string } | null;
  existingClients?: number;
}

export function ShareWithClients({ jobId }: { jobId: string }) {
  const { me } = useApp();
  const manager = !!me?.roles?.some((r) => r === 'customer_owner' || r === 'customer_admin');
  const state = useResource<ShareState>(manager ? `/v1/growth-audit/jobs/${encodeURIComponent(jobId)}/share` : null);
  if (!manager || state.status !== 'ready' || !state.data.internal) return null;
  return <ShareCard jobId={jobId} s={state.data} reload={state.reload} />;
}

export function ShareCard({ jobId, s, reload }: { jobId: string; s: ShareState; reload?: () => void }) {
  const { t } = useApp();
  const f = useFormat();
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  const host = s.host ?? '';
  const act = async (path: 'share' | 'unshare') => {
    setBusy(true); setMsg(null);
    try {
      const r = await bffJson<{ notified?: number }>(`/v1/growth-audit/jobs/${encodeURIComponent(jobId)}/${path}`, 'POST', {});
      setMsg({ tone: 'ok', text: path === 'share' ? t('shell.p1.share.done', { n: f.number(r?.notified ?? 0) }) : t('shell.p1.share.stopped') });
      reload?.();
    } catch (e) {
      setMsg({ tone: 'error', text: t(auditErrorKey(e)) });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card aria-labelledby="audit-share">
      <CardHead id="audit-share" title={t('shell.p1.share.title')}
        trailing={s.share ? <Badge tone="success">{t('shell.p1.share.on')}</Badge> : <Badge tone="neutral">{t('shell.p1.share.off')}</Badge>} />
      {s.refusal ? <Text tone="muted">{t(`shell.p1.share.refusal.${s.refusal}`)}</Text> : s.share ? (
        <>
          <Text>{t('shell.p1.share.sharedSince', { host, date: f.date(s.share.approvedAt) })}</Text>
          <Button disabled={busy} onClick={() => void act('unshare')}>{t('shell.p1.share.stop')}</Button>
        </>
      ) : (
        <>
          <Text>{t('shell.p1.share.explain', { host })}</Text>
          <Text tone="muted">{t('shell.p1.share.existing', { n: f.number(s.existingClients ?? 0) })}</Text>
          <Button variant="primary" disabled={busy || !s.ready} onClick={() => void act('share')}>{t('shell.p1.share.approve', { host })}</Button>
        </>
      )}
      {msg ? <Text role={msg.tone === 'error' ? 'alert' : 'status'}>{msg.text}</Text> : null}
    </Card>
  );
}
